import {FC, useState, useEffect, useRef} from 'react'
import {Outlet, NavLink, useNavigate, useLocation} from 'react-router-dom'
import {useAdminAuth} from './auth/AdminAuthContext'
import {getAdminToken} from './auth/_requests'
import './tickets/admin-tickets.scss'
import MeetingsBubble from './meetings/MeetingsBubble'
import TasksBubble from './tasks/TasksBubble'
import PaymentTasksBubble from './payments/PaymentTasksBubble'
import NotificationBell from './NotificationBell'
import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL || ''

// Stats interface for sidebar badges
interface SidebarStats {
  totalOpen: number
  totalUnassigned: number
  myTickets: number
}

interface ClientAccount {
  _id: string
  first_name: string
  last_name: string
  email: string
  phone: string
}

export const AdminLayout: FC = () => {
  const {admin, logout} = useAdminAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [showDropdown, setShowDropdown] = useState(false)
  const [stats, setStats] = useState<SidebarStats>({totalOpen: 0, totalUnassigned: 0, myTickets: 0})
  const [postAlerts, setPostAlerts] = useState(0)
  const [clientAccount, setClientAccount] = useState<ClientAccount | null>(null)
  const [showSwitcher, setShowSwitcher] = useState(false)
  const [switcherSearch, setSwitcherSearch] = useState('')
  const [allAccounts, setAllAccounts] = useState<ClientAccount[]>([])
  const [switching, setSwitching] = useState<string | null>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const switcherRef = useRef<HTMLDivElement>(null)

  // Set body background for admin pages only
  useEffect(() => {
    const originalBg = document.body.style.background
    document.body.style.background = '#f5f8fa'
    return () => {
      document.body.style.background = originalBg
    }
  }, [])

  // Fetch stats for sidebar badges
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL
        const token = getAdminToken()
        const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {}
        const response = await fetch(`${API_URL}/admin/tickets/stats`, {
          credentials: 'include',
          headers
        })
        if (response.ok) {
          const data = await response.json()
          setStats({
            totalOpen: data.totalOpen || 0,
            totalUnassigned: data.totalUnassigned || 0,
            myTickets: data.myTickets || 0
          })
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error)
      }
    }
    fetchStats()
  }, [location.pathname])

  // Red badge on "פרסומים מאושרים": clients whose posts are not going out
  // (server caches this for 5 min, so polling per navigation is cheap).
  useEffect(() => {
    const token = getAdminToken()
    fetch(`${API_URL}/admin/post-confirmations/alerts-count`, {
      credentials: 'include',
      headers: token ? {Authorization: `Bearer ${token}`} : {},
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setPostAlerts(d.red || 0) })
      .catch(() => {})
  }, [location.pathname])

  // Get current client account from client auth token
  useEffect(() => {
    try {
      const lsValue = localStorage.getItem('kt-auth-react-v')
      if (!lsValue) return
      const auth = JSON.parse(lsValue)
      if (!auth?.api_token) return
      // Decode JWT payload (base64)
      const payload = JSON.parse(atob(auth.api_token.split('.')[1]))
      if (payload?.user?._id) {
        const token = getAdminToken()
        const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}
        axios.get(`${API_URL}/admin/accounts/${payload.user._id}`, {
          headers,
          withCredentials: true
        }).then(res => {
          if (res.data?.account) setClientAccount(res.data.account)
        }).catch(() => {})
      }
    } catch {}
  }, [])

  // Load all accounts for switcher
  useEffect(() => {
    const token = getAdminToken()
    if (!token) return
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` }
    axios.get(`${API_URL}/admin/search-accounts?light=1`, {
      headers,
      withCredentials: true
    }).then(res => {
      setAllAccounts(res.data?.accounts || [])
    }).catch(() => {})
  }, [])

  // Close switcher on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (switcherRef.current && !switcherRef.current.contains(e.target as Node)) {
        setShowSwitcher(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const filteredAccounts = switcherSearch.length >= 1
    ? allAccounts.filter(a => {
        const q = switcherSearch.toLowerCase()
        const fullName = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase()
        return fullName.includes(q) ||
          (a.first_name || '').toLowerCase().includes(q) ||
          (a.last_name || '').toLowerCase().includes(q) ||
          (a.email || '').toLowerCase().includes(q) ||
          (a.phone || '').toLowerCase().includes(q)
      })
    : allAccounts

  const handleSwitchAccount = async (accountId: string) => {
    setSwitching(accountId)
    try {
      const token = getAdminToken()
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}
      const response = await axios.get(`${API_URL}/admin/switch-account/${accountId}`, {
        headers,
        withCredentials: true
      })
      if (response.data.success && response.data.token) {
        window.location.href = '/auth/auto-login?token=' + encodeURIComponent(response.data.token)
      }
    } catch (error) {
      console.error('Switch account error:', error)
    } finally {
      setSwitching(null)
    }
  }

  const handleLogout = async () => {
    await logout()
    window.location.href = '/admin/login'
  }

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname, location.search])

  const getNavClass = (path: string, exact = false) => {
    const isActive = exact
      ? location.pathname === path
      : location.pathname.startsWith(path)
    return `nav-item ${isActive ? 'active' : ''}`
  }

  const getFilterNavClass = (filter: string) => {
    const searchParams = new URLSearchParams(location.search)
    const currentFilter = searchParams.get('filter')
    return `nav-item ${currentFilter === filter ? 'active' : ''}`
  }

  return (
    <div className="admin-shell">
      {/* Header */}
      <header className="admin-header">
        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <i className={`bi ${mobileMenuOpen ? 'bi-x-lg' : 'bi-list'}`}></i>
          </button>
          <div className="header-logo">
            <div className="logo-icon">
              <i className="bi bi-headset"></i>
            </div>
            <span>Ticket System</span>
          </div>
        </div>

        <div className="header-actions">
          {clientAccount && (
            <a
              href="/campaigns"
              className="client-badge"
              title="חזור לאזור הלקוח"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 14px',
                borderRadius: '8px',
                background: '#e8f4fd',
                border: '1px solid #009ef7',
                textDecoration: 'none',
                marginLeft: '12px',
                cursor: 'pointer'
              }}
            >
              <i className="bi bi-person-fill" style={{fontSize: '16px', color: '#009ef7'}}></i>
              <div style={{display: 'flex', flexDirection: 'column', lineHeight: 1.3}}>
                <span style={{fontWeight: 600, fontSize: '13px', color: '#009ef7'}}>
                  {clientAccount.first_name} {clientAccount.last_name}
                </span>
                <span style={{fontSize: '11px', color: '#7e8299'}}>{clientAccount.email}</span>
              </div>
              <i className="bi bi-box-arrow-left" style={{fontSize: '16px', color: '#009ef7', marginRight: '4px'}}></i>
            </a>
          )}

          {/* Account Switcher */}
          <div style={{position: 'relative'}} ref={switcherRef}>
            <div
              onClick={() => setShowSwitcher(!showSwitcher)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 14px',
                borderRadius: '8px',
                background: showSwitcher ? '#fff4de' : '#f5f8fa',
                border: showSwitcher ? '1px solid #ffc107' : '1px solid #e4e6ef',
                cursor: 'pointer',
                marginLeft: '8px'
              }}
            >
              <i className="bi bi-arrow-left-right" style={{fontSize: '14px', color: showSwitcher ? '#ffc107' : '#7e8299'}}></i>
              <span style={{fontWeight: 600, fontSize: '13px', color: showSwitcher ? '#ffc107' : '#7e8299'}}>החלף לקוח</span>
            </div>
            {showSwitcher && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '8px',
                width: '380px',
                maxHeight: '480px',
                background: '#fff',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                border: '1px solid #e4e6ef',
                zIndex: 1050,
                overflow: 'hidden'
              }}>
                <div style={{padding: '12px', borderBottom: '1px solid #f1f1f2', background: '#f9fafb'}}>
                  <input
                    type="text"
                    placeholder="חפש לפי שם, אימייל או טלפון..."
                    value={switcherSearch}
                    onChange={(e) => setSwitcherSearch(e.target.value)}
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #e4e6ef',
                      borderRadius: '8px',
                      fontSize: '13px',
                      outline: 'none',
                      direction: 'rtl'
                    }}
                  />
                </div>
                <div style={{maxHeight: '380px', overflowY: 'auto'}}>
                  {filteredAccounts.length === 0 ? (
                    <div style={{padding: '24px', textAlign: 'center', color: '#a1a5b7'}}>
                      לא נמצאו חשבונות
                    </div>
                  ) : filteredAccounts.map((account) => (
                    <div
                      key={account._id}
                      onClick={() => handleSwitchAccount(account._id)}
                      style={{
                        padding: '10px 14px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid #f5f5f5',
                        direction: 'rtl'
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <div>
                        <div style={{fontWeight: 600, fontSize: '13px', color: '#181c32'}}>
                          {account.first_name} {account.last_name}
                        </div>
                        <div style={{fontSize: '11px', color: '#a1a5b7'}}>{account.email}</div>
                        {(() => {
                          // Shared account comment (same store as the payments page). Show the latest one.
                          const notes = (account as any).important_notes
                          const latest = Array.isArray(notes) && notes.length ? notes[notes.length - 1] : null
                          return latest?.text ? (
                            <div style={{fontSize: '10px', color: '#b26a00', background: '#fff8e1', border: '1px solid #ffe0a3', borderRadius: '5px', padding: '2px 6px', marginTop: '3px', maxWidth: '260px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}} title={latest.text}>
                              📝 {latest.text}
                            </div>
                          ) : null
                        })()}
                      </div>
                      {switching === account._id ? (
                        <span className="spinner-border spinner-border-sm text-primary" />
                      ) : (
                        <i className="bi bi-arrow-left-circle" style={{fontSize: '18px', color: '#009ef7'}}></i>
                      )}
                    </div>
                  ))}
                </div>
                <div style={{padding: '8px', borderTop: '1px solid #f1f1f2', background: '#f9fafb', textAlign: 'center'}}>
                  <span style={{fontSize: '11px', color: '#a1a5b7'}}>
                    {filteredAccounts.length} מתוך {allAccounts.length} חשבונות
                  </span>
                </div>
              </div>
            )}
          </div>

          <NotificationBell />

          <div className="admin-dropdown">
            <div
              className="admin-avatar"
              onClick={() => setShowDropdown(!showDropdown)}
            >
              {admin?.username?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div className={`dropdown-menu ${showDropdown ? 'show' : ''}`}>
              <div className="dropdown-item" style={{cursor: 'default', opacity: 0.7}}>
                <i className="bi bi-person"></i>
                <span>{admin?.username || 'Admin'}</span>
              </div>
              <div className="dropdown-divider"></div>
              <div className="dropdown-item danger" onClick={handleLogout}>
                <i className="bi bi-box-arrow-right"></i>
                <span>התנתק</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile sidebar overlay */}
      <div className={`sidebar-overlay ${mobileMenuOpen ? 'open' : ''}`} onClick={() => setMobileMenuOpen(false)} />

      {/* Sidebar */}
      <aside className={`admin-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-section">
          <div className="section-title">ניהול</div>

          <NavLink
            to="/admin/accounts"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>חשבונות</span>
            <span className="nav-icon">
              <i className="bi bi-people"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/issues"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>תקלות</span>
            <span className="nav-icon">
              <i className="bi bi-exclamation-triangle"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/logged-out-profiles"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>פרופילים מנותקים</span>
            <span className="nav-icon">
              <i className="bi bi-box-arrow-in-right"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/login-attempts"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>ניסיונות התחברות</span>
            <span className="nav-icon">
              <i className="bi bi-shield-lock"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/payments"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>תשלומות</span>
            <span className="nav-icon">
              <i className="bi bi-credit-card-2-front"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/payment-breakdown"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>פירוט תשלומים</span>
            <span className="nav-icon">
              <i className="bi bi-bar-chart-line"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/affiliate-payouts"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>תשלום לאפילייטים</span>
            <span className="nav-icon">
              <i className="bi bi-cash-stack"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/referral-promo"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>תוכנית הפניות (פופאפ/SMS)</span>
            <span className="nav-icon">
              <i className="bi bi-megaphone"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/sms-campaigns"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>קמפיין SMS</span>
            <span className="nav-icon">
              <i className="bi bi-chat-dots"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/api-keys"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>מפתחות API</span>
            <span className="nav-icon">
              <i className="bi bi-key"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/referrers"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>מי הביא מי (הפניות)</span>
            <span className="nav-icon">
              <i className="bi bi-people"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/virtual-numbers"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>מספרים וירטואליים</span>
            <span className="nav-icon">
              <i className="bi bi-telephone-forward"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/ads"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>מודעות פייסבוק</span>
            <span className="nav-icon">
              <i className="bi bi-megaphone"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/whatsapp"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>וואטסאפ</span>
            <span className="nav-icon">
              <i className="bi bi-whatsapp"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/whatsapp-grid"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>וואטסאפ — כל השיחות (ניסיון)</span>
            <span className="nav-icon">
              <i className="bi bi-grid-3x3-gap"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/scheduler"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>תזמון קמפיינים</span>
            <span className="nav-icon">
              <i className="bi bi-calendar-week"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/post-pacing"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>קצב פרסום</span>
            <span className="nav-icon">
              <i className="bi bi-speedometer2"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/post-confirmations"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>פרסומים מאושרים</span>
            <span className="nav-icon">
              <i className="bi bi-patch-check"></i>
            </span>
            {postAlerts > 0 && (
              <span className="nav-badge">{postAlerts}</span>
            )}
          </NavLink>

          <NavLink
            to="/admin/libraries"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>ספריות קבוצות</span>
            <span className="nav-icon">
              <i className="bi bi-collection"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/campaign-categorization"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>סיווג קמפיינים</span>
            <span className="nav-icon">
              <i className="bi bi-tags"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/sales-agents"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>לקוחות נדל״ן לפי עיר</span>
            <span className="nav-icon">
              <i className="bi bi-buildings"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/account-city-search"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>חיפוש לקוחות לפי עיר</span>
            <span className="nav-icon">
              <i className="bi bi-search"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/sales-library"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>ספריית מכירות</span>
            <span className="nav-icon">
              <i className="bi bi-mic"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/sales-training"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>הדרכות מכירה</span>
            <span className="nav-icon">
              <i className="bi bi-headphones"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/marketplace"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>מרקטפלייס</span>
            <span className="nav-icon">
              <i className="bi bi-shop-window"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/subprofiles"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>תתי דפים</span>
            <span className="nav-icon">
              <i className="bi bi-layers"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/scheduled-subprofiles"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>תתי דפים משובצים</span>
            <span className="nav-icon">
              <i className="bi bi-calendar-check"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/duplicate-profiles"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>פרופילים כפולים</span>
            <span className="nav-icon">
              <i className="bi bi-files"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/meetings"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>פגישות</span>
            <span className="nav-icon">
              <i className="bi bi-calendar-event"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/request-logs"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>בקשות פרופילים</span>
            <span className="nav-icon">
              <i className="bi bi-activity"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/curl-to-cookies"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>Curl to Cookies</span>
            <span className="nav-icon">
              <i className="bi bi-terminal"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/settings"
            className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <span>הגדרות</span>
            <span className="nav-icon">
              <i className="bi bi-gear"></i>
            </span>
          </NavLink>

          {admin?.is_super_admin && (
            <NavLink
              to="/admin/admin-users"
              className={({isActive}) => `nav-item ${isActive ? 'active' : ''}`}
            >
              <span>משתמשי אדמין</span>
              <span className="nav-icon">
                <i className="bi bi-shield-lock"></i>
              </span>
            </NavLink>
          )}
        </div>

        <div className="sidebar-section">
          <div className="section-title">פניות</div>

          <NavLink
            to="/admin/tickets?filter=all"
            className={({isActive}) => {
              const searchParams = new URLSearchParams(location.search)
              const filter = searchParams.get('filter')
              return `nav-item ${!filter || filter === 'all' ? 'active' : ''}`
            }}
          >
            <span>כל הפניות</span>
            <span className="nav-icon">
              <i className="bi bi-inbox"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/tickets?filter=open"
            className={() => getFilterNavClass('open')}
          >
            <span>פתוחות</span>
            <span className="nav-icon">
              <i className="bi bi-envelope-open"></i>
            </span>
            {stats.totalOpen > 0 && (
              <span className="nav-badge">{stats.totalOpen}</span>
            )}
          </NavLink>

          <NavLink
            to="/admin/tickets?filter=unassigned"
            className={() => getFilterNavClass('unassigned')}
          >
            <span>לא משויכות</span>
            <span className="nav-icon">
              <i className="bi bi-person-x"></i>
            </span>
            {stats.totalUnassigned > 0 && (
              <span className="nav-badge">{stats.totalUnassigned}</span>
            )}
          </NavLink>

          <NavLink
            to="/admin/tickets?filter=my"
            className={() => getFilterNavClass('my')}
          >
            <span>הפניות שלי</span>
            <span className="nav-icon">
              <i className="bi bi-person-badge"></i>
            </span>
            {stats.myTickets > 0 && (
              <span className="nav-badge" style={{background: 'var(--accent-primary, #3699ff)'}}>{stats.myTickets}</span>
            )}
          </NavLink>
        </div>

        <div className="sidebar-section">
          <div className="section-title">סטטוס</div>

          <NavLink
            to="/admin/tickets?status=in_progress"
            className={() => {
              const searchParams = new URLSearchParams(location.search)
              return `nav-item ${searchParams.get('status') === 'in_progress' ? 'active' : ''}`
            }}
          >
            <span>בטיפול</span>
            <span className="nav-icon">
              <i className="bi bi-hourglass-split"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/tickets?status=waiting_on_customer"
            className={() => {
              const searchParams = new URLSearchParams(location.search)
              return `nav-item ${searchParams.get('status') === 'waiting_on_customer' ? 'active' : ''}`
            }}
          >
            <span>ממתין ללקוח</span>
            <span className="nav-icon">
              <i className="bi bi-clock"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/tickets?status=resolved"
            className={() => {
              const searchParams = new URLSearchParams(location.search)
              return `nav-item ${searchParams.get('status') === 'resolved' ? 'active' : ''}`
            }}
          >
            <span>נפתרו</span>
            <span className="nav-icon">
              <i className="bi bi-check-circle"></i>
            </span>
          </NavLink>

          <NavLink
            to="/admin/tickets?status=closed"
            className={() => {
              const searchParams = new URLSearchParams(location.search)
              return `nav-item ${searchParams.get('status') === 'closed' ? 'active' : ''}`
            }}
          >
            <span>סגורות</span>
            <span className="nav-icon">
              <i className="bi bi-archive"></i>
            </span>
          </NavLink>
        </div>

        <div className="sidebar-footer">
          <div
            className="nav-item"
            onClick={handleLogout}
            style={{cursor: 'pointer'}}
          >
            <span>התנתק</span>
            <span className="nav-icon">
              <i className="bi bi-box-arrow-right"></i>
            </span>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="admin-main">
        <Outlet />
      </main>

      {/* Floating bubbles */}
      <MeetingsBubble />
      <TasksBubble />
      <PaymentTasksBubble />

      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99
          }}
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  )
}
